import { Flex, Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting } from '@feathr/blackbox';
import { FormSummaryItem, Heading, Pill, Skeleton, Text, Well } from '@feathr/components';
import GeoFiltersList from '@feathr/extender/components/Targetings/Locations/GeoFiltersList';
import {
  extractGeoFilterIdsFromTargetings,
  getGeoTargetings,
} from '@feathr/extender/components/Targetings/Targetings.utils';
import { useStore } from '@feathr/extender/state';

import { CampaignContext } from '../CampaignEditPage/CampaignEditPage.context';

interface IProps {
  campaign: Campaign;
  validationErrors?: {
    filters?: string[];
  };
}

function filterAgeDemographicTargetings(trgt: Targeting): boolean {
  return (
    trgt.get('kind') === 'age_demographic' && !trgt.get('is_archived') && !!trgt.get('target_data')
  );
}

function filterInterestTargetings(trgt: Targeting): boolean {
  return trgt.get('kind') === 'interest' && !trgt.get('is_archived') && !!trgt.get('target_data');
}

const FiltersSummary = observer(({ validationErrors, campaign }: IProps): JSX.Element => {
  const { t } = useTranslation();
  const { Targetables } = useStore();
  const { targetings } = useContext(CampaignContext);
  const theme = useMantineTheme();
  const { isOmniCampaign, isOmniReachCampaign } = campaign;
  const none = t('(None)');

  // Compute geo filter IDs synchronously during render so MobX can track
  // the observable reads and trigger re-renders when data loads.
  const geoTargetingsList = targetings ? getGeoTargetings(targetings.models) : [];
  const geoSetTargeting = geoTargetingsList.find(
    (targeting) => targeting.get('kind') === 'geo_set',
  );

  let geoFilterIds: { included: string[]; excluded: string[]; geosetName?: string } = {
    included: [],
    excluded: [],
  };
  let isLoadingGeoFilters = !!targetings?.isPending;

  if (geoSetTargeting) {
    const targetData = geoSetTargeting.get('target_data');
    if (targetData) {
      const geoSetTargetable = Targetables.get(targetData);
      if (geoSetTargetable.isPending) {
        isLoadingGeoFilters = true;
      } else {
        geoFilterIds = {
          included: geoSetTargetable.get('included_geo_filters') ?? [],
          excluded: geoSetTargetable.get('excluded_geo_filters') ?? [],
          geosetName: geoSetTargetable.get('name'),
        };
      }
    }
  } else if (geoTargetingsList.length > 0) {
    const geoTargetables = geoTargetingsList.flatMap((t) => {
      const targetableId = t.get('target_data');
      return targetableId ? [Targetables.get(targetableId)] : [];
    });

    if (geoTargetables.some((targetable) => targetable.isPending)) {
      isLoadingGeoFilters = true;
    } else {
      geoFilterIds = extractGeoFilterIdsFromTargetings(geoTargetingsList, Targetables);
    }
  }

  const ageDemographicFilteredTargetings =
    targetings?.models.filter(filterAgeDemographicTargetings) ?? [];
  const interestFilteredTargetings = targetings?.models.filter(filterInterestTargetings) ?? [];

  const hasTargetings =
    geoTargetingsList.length > 0 ||
    geoFilterIds.included.length > 0 ||
    geoFilterIds.excluded.length > 0 ||
    ageDemographicFilteredTargetings.length > 0 ||
    interestFilteredTargetings.length > 0;

  const ageDemographicTargetingListItems = ageDemographicFilteredTargetings.map((ad) => {
    return (
      <Pill key={ad.id} truncate={false}>
        {ad.get('name')} years
      </Pill>
    );
  });

  const interestTargetingListItems = interestFilteredTargetings.map((interest) => {
    return (
      <Pill key={interest.id} truncate={false}>
        {interest.get('name')}
      </Pill>
    );
  });

  let geoFiltersValue: ReactNode = <p>{none}</p>;
  if (isLoadingGeoFilters) {
    geoFiltersValue = <Skeleton paragraphs={3} title={true} width={'100%'} />;
  } else if (geoFilterIds.included.length > 0 || geoFilterIds.excluded.length > 0) {
    geoFiltersValue = (
      <GeoFiltersList
        excludedGeoFilterIds={geoFilterIds.excluded}
        includedGeoFilterIds={geoFilterIds.included}
      />
    );
  }

  const geoFiltersWell = (
    <Well data-name={'filters_locations'} layout={'vertical'}>
      <Stack gap={theme.spacing[2]}>
        <div>
          <Heading order={5} variant={'h5'}>
            {geoSetTargeting ? t('Geofilter Set Name') : t('Locations')}
          </Heading>
          {geoFilterIds.geosetName && (
            <Text.DefaultRegular>{geoFilterIds.geosetName}</Text.DefaultRegular>
          )}
        </div>
        {geoFiltersValue}
      </Stack>
    </Well>
  );

  const omniCampaignFilters = (
    <Stack gap={theme.spacing[2]}>
      {geoFiltersWell}
      <Well data-name={'filters_age_demographics'} layout={'vertical'}>
        <Stack gap={theme.spacing[1]}>
          <Heading order={5} variant={'h5'}>
            {t('Age Demographics')}
          </Heading>
          <Flex gap={theme.spacing['2']} wrap={'wrap'}>
            {ageDemographicTargetingListItems.length > 0 ? (
              ageDemographicTargetingListItems
            ) : (
              <p>{none}</p>
            )}
          </Flex>
        </Stack>
      </Well>
      <Well data-name={'filters_interests'} layout={'vertical'}>
        <Stack gap={theme.spacing[1]}>
          <Heading order={5} variant={'h5'}>
            {t('Interests')}
          </Heading>
          <Flex gap={theme.spacing['2']} wrap={'wrap'}>
            {interestTargetingListItems.length > 0 ? interestTargetingListItems : <p>{none}</p>}
          </Flex>
        </Stack>
      </Well>
    </Stack>
  );

  const locationFilters = geoFiltersWell;

  const formSummary = isOmniCampaign ? omniCampaignFilters : locationFilters;

  let summaryValue: ReactNode = none;
  if (targetings?.isPending) {
    summaryValue = <Skeleton width={100} />;
  } else if (hasTargetings) {
    summaryValue = formSummary;
  }

  return (
    <FormSummaryItem
      errors={validationErrors?.filters}
      label={isOmniReachCampaign ? t('Audience') : t('Filters')}
      name={'summary_filters'}
      value={summaryValue}
    />
  );
});

FiltersSummary.displayName = 'FiltersSummary';

export default FiltersSummary;
